/**
 * Plataforma → Bundles (planos de contratação).
 *
 * Edição de nome/preço/limites e do stripe_price_id de cada plano
 * (features/billing, migration 0040). O código do plano é imutável.
 */
import { useEffect, useState } from 'react'
import { platform, type PlanDto } from '../lib/api.js'
import {
  Button,
  Input,
  Card,
  Badge,
  toast,
  Toaster,
  Spinner,
  Empty,
} from '../components/ui/index.js'
import { useI18n } from '../lib/i18n.js'

export function PlatformPlans() {
  const { t } = useI18n()
  const [plans, setPlans] = useState<PlanDto[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<(PlanDto & { price_cents: number }) | null>(null)
  const [saving, setSaving] = useState(false)

  async function load() {
    setLoading(true)
    try {
      setPlans(await platform.listPlans())
    } catch {
      toast.error(t('admin.plans.load_err'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  async function save(e: React.FormEvent) {
    e.preventDefault()
    if (!editing) return
    setSaving(true)
    try {
      await platform.updatePlan(editing.code, {
        name: editing.name,
        description: editing.description || null,
        price_cents: Number(editing.price_cents) || 0,
        currency: editing.currency || 'EUR',
        stripe_price_id: editing.stripe_price_id || null,
        // input vazio grava null = ilimitado (tradução: todos os idiomas)
        max_kitchen: editing.max_kitchen,
        max_waiters: editing.max_waiters,
        // Gerentes (#286): 0 = sem perfil Gerente; vazio = ilimitado
        max_managers: editing.max_managers,
        translation_locales: editing.translation_locales,
        is_active: editing.is_active,
      })
      toast.success(t('admin.plans.saved_toast'))
      setEditing(null)
      load()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('admin.plans.save_err'))
    } finally {
      setSaving(false)
    }
  }

  const limitLabel = (v: number | null) => (v === null ? t('admin.plans.unlimited') : String(v))

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <Toaster />
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('admin.plans.title')}</h1>
        <p className="text-sm text-gray-500 mt-0.5">{t('admin.plans.subtitle')}</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <Spinner className="w-8 h-8 text-indigo-600" />
        </div>
      ) : plans.length === 0 ? (
        <Empty title={t('admin.plans.empty')} description={t('admin.plans.empty_hint')} />
      ) : (
        <Card>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                {[
                  'plan',
                  'price',
                  'kitchen',
                  'waiters',
                  'managers',
                  'translations',
                  'stripe',
                  'status',
                ].map((h) => (
                  <th key={h} className="text-left py-3 px-3 font-medium text-gray-500">
                    {t(`admin.plans.col_${h}`)}
                  </th>
                ))}
                <th />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {plans.map((p) => (
                <tr key={p.code} className="hover:bg-gray-50">
                  <td className="py-3 px-3">
                    <p className="font-medium text-gray-900">{p.name}</p>
                    <p className="text-xs text-gray-400 font-mono">{p.code}</p>
                  </td>
                  <td className="py-3 px-3 text-gray-700">
                    {p.price_cents === 0
                      ? t('admin.plans.free')
                      : `${(p.price_cents / 100).toFixed(2)} ${p.currency}`}
                  </td>
                  <td className="py-3 px-3 text-gray-700">{limitLabel(p.max_kitchen)}</td>
                  <td className="py-3 px-3 text-gray-700">{limitLabel(p.max_waiters)}</td>
                  <td className="py-3 px-3 text-gray-700" data-testid={`plan-managers-${p.code}`}>
                    {limitLabel(p.max_managers)}
                  </td>
                  <td className="py-3 px-3 text-gray-700">
                    {p.translation_locales === null
                      ? t('admin.plans.all_languages')
                      : limitLabel(p.translation_locales)}
                  </td>
                  <td className="py-3 px-3">
                    {p.stripe_price_id ? (
                      <span className="font-mono text-xs text-gray-500">{p.stripe_price_id}</span>
                    ) : (
                      <Badge color="gray">{t('admin.plans.no_stripe')}</Badge>
                    )}
                  </td>
                  <td className="py-3 px-3">
                    <Badge color={p.is_active ? 'green' : 'red'}>
                      {p.is_active ? t('admin.plans.active') : t('admin.plans.inactive')}
                    </Badge>
                  </td>
                  <td className="py-3 px-3 text-right">
                    <Button size="sm" variant="secondary" onClick={() => setEditing({ ...p })}>
                      {t('admin.plans.edit')}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
          <form
            onSubmit={save}
            className="bg-white rounded-xl max-w-lg w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto"
          >
            <h2 className="text-lg font-bold text-gray-900">
              {t('admin.plans.edit_title', { name: editing.name })}
            </h2>
            <Input
              label={t('admin.plans.col_plan')}
              value={editing.name}
              onChange={(e) => setEditing({ ...editing, name: e.target.value })}
              required
            />
            <Input
              label={t('admin.plans.description')}
              value={editing.description ?? ''}
              onChange={(e) => setEditing({ ...editing, description: e.target.value })}
            />
            <div className="grid grid-cols-2 gap-3">
              <Input
                label={t('admin.plans.price_cents')}
                type="number"
                min={0}
                value={String(editing.price_cents)}
                onChange={(e) => setEditing({ ...editing, price_cents: Number(e.target.value) })}
              />
              <Input
                label={t('admin.plans.currency')}
                value={editing.currency}
                onChange={(e) => setEditing({ ...editing, currency: e.target.value.toUpperCase() })}
                maxLength={3}
              />
            </div>
            <Input
              label={t('admin.plans.stripe_price_id')}
              value={editing.stripe_price_id ?? ''}
              onChange={(e) => setEditing({ ...editing, stripe_price_id: e.target.value })}
              placeholder="price_..."
            />
            <div className="grid grid-cols-3 gap-3">
              <Input
                label={t('admin.plans.col_kitchen')}
                type="number"
                min={1}
                value={String(editing.max_kitchen ?? '')}
                onChange={(e) =>
                  setEditing({
                    ...editing,
                    max_kitchen: e.target.value === '' ? null : Number(e.target.value),
                  })
                }
                placeholder={t('admin.plans.unlimited')}
              />
              <Input
                label={t('admin.plans.col_waiters')}
                type="number"
                min={1}
                value={String(editing.max_waiters ?? '')}
                onChange={(e) =>
                  setEditing({
                    ...editing,
                    max_waiters: e.target.value === '' ? null : Number(e.target.value),
                  })
                }
                placeholder={t('admin.plans.unlimited')}
              />
              <Input
                label={t('admin.plans.col_managers')}
                hint={t('admin.plans.managers_hint')}
                type="number"
                min={0}
                data-testid="plan-edit-managers"
                value={String(editing.max_managers ?? '')}
                onChange={(e) =>
                  setEditing({
                    ...editing,
                    max_managers: e.target.value === '' ? null : Number(e.target.value),
                  })
                }
                placeholder={t('admin.plans.unlimited')}
              />
              <Input
                label={t('admin.plans.col_translations')}
                type="number"
                min={0}
                value={String(editing.translation_locales ?? '')}
                onChange={(e) =>
                  setEditing({
                    ...editing,
                    translation_locales: e.target.value === '' ? null : Number(e.target.value),
                  })
                }
                placeholder={t('admin.plans.all_languages')}
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={editing.is_active}
                onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })}
              />
              {t('admin.plans.active')}
            </label>
            <div className="flex gap-3 justify-end pt-2">
              <Button type="button" variant="secondary" onClick={() => setEditing(null)}>
                {t('admin.common.cancel')}
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? t('admin.plans.saving') : t('admin.plans.save')}
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  )
}
